export type TMcsaSfx =
    | 'click'
    | 'select'
    | 'unlock'
    | 'chime'
    | 'whoosh'
    | 'skip'
    | 'buzz'
    | 'teaser';

export type TMcsaTone = 'good' | 'bad';

let muted = false;
let audioCtx: AudioContext | null = null;

export const setMcsaMuted = (next: boolean) => {
    muted = next;
};

export const isMcsaMuted = () => muted;

const context = () => {
    if (muted) return null;
    try {
        const Ctor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return null;
        if (!audioCtx) audioCtx = new Ctor();
        if (audioCtx.state === 'suspended') audioCtx.resume();
        return audioCtx;
    } catch {
        return null;
    }
};

const tone = (freq: number, dur: number, type: OscillatorType = 'sine', vol = 0.14, delay = 0) => {
    const ac = context();
    if (!ac) return;
    const start = ac.currentTime + delay;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(vol, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain).connect(ac.destination);
    osc.start(start);
    osc.stop(start + dur + 0.03);
};

const sweep = (from: number, to: number, dur: number, vol = 0.1) => {
    const ac = context();
    if (!ac) return;
    const start = ac.currentTime;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(from, start);
    osc.frequency.exponentialRampToValueAtTime(to, start + dur);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(vol, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain).connect(ac.destination);
    osc.start(start);
    osc.stop(start + dur + 0.03);
};

export const playMcsaSfx = (name: TMcsaSfx) => {
    switch (name) {
        case 'click':
            tone(430, 0.08, 'triangle', 0.1);
            break;
        case 'select':
            tone(620, 0.09, 'sine', 0.13);
            tone(880, 0.12, 'sine', 0.09, 0.05);
            break;
        case 'unlock':
            [523, 659, 784, 1047].forEach((freq, index) => tone(freq, 0.28, 'triangle', 0.12, index * 0.09));
            break;
        case 'chime':
            tone(784, 0.3, 'sine', 0.12);
            tone(988, 0.4, 'sine', 0.1, 0.08);
            tone(1319, 0.5, 'sine', 0.08, 0.16);
            break;
        case 'whoosh':
            sweep(300, 900, 0.4, 0.08);
            break;
        case 'skip':
            sweep(500, 220, 0.28, 0.1);
            break;
        case 'buzz':
            tone(160, 0.14, 'sawtooth', 0.08);
            break;
        case 'teaser':
            tone(660, 0.12, 'triangle', 0.11);
            tone(990, 0.18, 'triangle', 0.09, 0.09);
            break;
        default:
            break;
    }
};

export const playMcsaTone = (kind: TMcsaTone) => {
    const ac = context();
    if (!ac) return;
    try {
        const osc = ac.createOscillator();
        const gain = ac.createGain();
        osc.connect(gain);
        gain.connect(ac.destination);
        const start = ac.currentTime;
        if (kind === 'good') {
            osc.frequency.setValueAtTime(760, start);
            osc.frequency.setValueAtTime(980, start + 0.08);
            gain.gain.setValueAtTime(0.09, start);
            gain.gain.exponentialRampToValueAtTime(0.001, start + 0.18);
            osc.start(start);
            osc.stop(start + 0.19);
            return;
        }
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(240, start);
        gain.gain.setValueAtTime(0.07, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.22);
        osc.start(start);
        osc.stop(start + 0.23);
    } catch {
        return;
    }
};
